import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { fetchCreditBillById, updateCreditBill } from "../../services/creditDueService";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function isoFromFirestoreDate(d) {
  if (!d) return "";
  if (d?.toDate) return d.toDate().toISOString().slice(0, 10);
  if (typeof d === "string") return d.slice(0, 10);
  return "";
}

export default function CreditEditBillPage() {
  const { billId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bill, setBill] = useState(null);
  const [err, setErr] = useState("");

  const [voucherNo, setVoucherNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [note, setNote] = useState("");
  const [careOf, setCareOf] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");

  const load = useCallback(async () => {
    if (!billId) return;
    setLoading(true);
    setErr("");
    try {
      const b = await fetchCreditBillById(billId);
      if (!b) {
        setErr("Bill not found.");
        setBill(null);
        return;
      }
      setBill(b);
      setVoucherNo(b.voucherNo || "");
      setInvoiceDate(typeof b.invoiceDate === "string" ? b.invoiceDate.slice(0, 10) : isoFromFirestoreDate(b.invoiceDate));
      setDueDate(isoFromFirestoreDate(b.dueDate));
      setTotalAmount(String(round2(b.totalAmount)));
      setNote(b.note || "");
      setCareOf(b.careOf || "");
      setCustomerName(b.customerName || "");
      setPhone(b.phone || "");
    } catch (e) {
      setErr(e?.message || "Failed to load bill.");
    } finally {
      setLoading(false);
    }
  }, [billId]);

  useEffect(() => {
    load();
  }, [load]);

  const manual = bill?.manual === true;
  const backHref = bill?.customerId
    ? `/credit-due/customer/${encodeURIComponent(bill.customerId)}`
    : "/credit-due";

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!bill) return;
    setSaving(true);
    setErr("");
    try {
      const patch = {
        note,
        careOf: careOf.trim() || null,
        dueDate: dueDate.trim() ? dueDate.trim().slice(0, 10) : null,
        customerName: customerName.trim(),
        phone: phone.trim(),
      };
      if (manual) {
        const inv = invoiceDate.trim();
        const vn = voucherNo.trim();
        const amt = parseFloat(totalAmount);
        if (!vn) throw new Error("Invoice / bill number is required");
        if (!inv) throw new Error("Invoice date is required");
        if (!Number.isFinite(amt) || amt <= 0) throw new Error("Enter a valid amount");
        patch.voucherNo = vn;
        patch.invoiceDate = inv;
        patch.totalAmount = amt;
      }
      await updateCreditBill(bill.id, patch);
      toast.success("Saved.");
      navigate(backHref);
    } catch (e) {
      const msg = e?.message || "Save failed.";
      setErr(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-lg px-3 pb-24 pt-4 sm:max-w-xl sm:px-4">
        <Link to={bill?.customerId ? backHref : "/credit-due"} className="text-sm font-semibold text-indigo-600 hover:underline">
          ← {bill?.customerId ? "Customer" : "Credit / Due"}
        </Link>
        <h1 className="mt-2 text-lg font-bold text-slate-900">Edit credit / due entry</h1>
        <p className="mt-1 text-xs text-slate-600 font-mono break-all">{billId}</p>

        {loading ? (
          <p className="mt-6 text-sm text-slate-600">Loading…</p>
        ) : !bill ? (
          <p className="mt-6 text-sm text-red-700">{err || "Bill not found."}</p>
        ) : (
          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${
                  manual ? "bg-violet-100 text-violet-900" : "bg-slate-200 text-slate-800"
                }`}
              >
                {manual ? "Manual Udhar" : "From invoice"}
              </span>
              {bill.invoiceId ? (
                <span className="text-xs text-slate-500">
                  Linked invoice id: <span className="font-mono">{bill.invoiceId}</span>
                </span>
              ) : null}
            </div>

            {err ? <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{err}</div> : null}

            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block text-sm font-semibold text-slate-800 sm:col-span-2">
                  Customer name <span className="font-normal text-slate-500">(display)</span>
                  <input
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    autoComplete="off"
                  />
                </label>
                <label className="block text-sm font-semibold text-slate-800 sm:col-span-2">
                  Phone
                  <input
                    type="text"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    autoComplete="off"
                  />
                </label>
              </div>

              {manual ? (
                <>
                  <label className="block text-sm font-semibold text-slate-800">
                    Invoice / bill no. <span className="text-red-600">*</span>
                    <input
                      type="text"
                      required
                      value={voucherNo}
                      onChange={(e) => setVoucherNo(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
                      autoComplete="off"
                    />
                  </label>
                  <label className="block text-sm font-semibold text-slate-800">
                    Invoice date <span className="text-red-600">*</span>
                    <input
                      type="date"
                      required
                      value={invoiceDate}
                      onChange={(e) => setInvoiceDate(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block text-sm font-semibold text-slate-800">
                    Amount (₹) <span className="text-red-600">*</span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      required
                      value={totalAmount}
                      onChange={(e) => setTotalAmount(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-right tabular-nums"
                    />
                    <span className="mt-1 block text-xs text-slate-500">
                      Already paid ₹{round2(bill.paidAmount).toLocaleString("en-IN")} — total cannot go below that.
                    </span>
                  </label>
                </>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p>
                    <span className="font-semibold text-slate-900">Bill no.:</span>{" "}
                    <span className="font-mono">{bill.voucherNo || "—"}</span>
                  </p>
                  <p className="mt-1">
                    <span className="font-semibold text-slate-900">Invoice date:</span>{" "}
                    {invoiceDate || isoFromFirestoreDate(bill.invoiceDate) || "—"}
                  </p>
                  <p className="mt-1 tabular-nums">
                    <span className="font-semibold text-slate-900">Total</span> ₹{round2(bill.totalAmount).toLocaleString("en-IN")} ·{" "}
                    <span className="font-semibold text-slate-900">Paid</span> ₹{round2(bill.paidAmount).toLocaleString("en-IN")} ·{" "}
                    <span className="font-semibold text-slate-900">Balance</span> ₹{round2(bill.balanceAmount).toLocaleString("en-IN")}
                  </p>
                  <p className="mt-2 text-xs text-slate-600">
                    Amounts come from the stock invoice. Change the due date or notes here; fix wrong totals from Billing if needed.
                  </p>
                </div>
              )}

              <label className="block text-sm font-semibold text-slate-800">
                Due date <span className="font-normal text-slate-500">(optional)</span>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>

              <label className="block text-sm font-semibold text-slate-800">
                C/o <span className="font-normal text-slate-500">(optional)</span>
                <input
                  type="text"
                  value={careOf}
                  onChange={(e) => setCareOf(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  autoComplete="off"
                  maxLength={200}
                />
              </label>

              <label className="block text-sm font-semibold text-slate-800">
                Note
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </label>

              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  type="button"
                  onClick={() => navigate(backHref)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
